import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

export interface RescheduleComplianceTaskModalRequest {
  dueDate: string;
  notifyPersonResponsible: boolean;
}

@Component({
  selector: 'app-reschedule-compliance-task-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlatpickrDatePickerComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './reschedule-compliance-task-modal.component.html',
  styleUrl: './reschedule-compliance-task-modal.component.scss'
})
export class RescheduleComplianceTaskModalComponent implements OnInit, OnDestroy {
  private transloco = inject(TranslocoService);

  @Input() initialDate?: string | Date | null;
  @Input() showNotifyPersonResponsible = true;
  @Input() enableTime = false;
  @Input() successMessage = this.transloco.translate('compliance.shared.modals.reschedule.successToast');
  @Input() submitHandler!: (
    request: RescheduleComplianceTaskModalRequest,
    cancellation: Subject<void>
  ) => Promise<void>;

  rescheduleForm!: FormGroup;
  isLoading = false;
  dateValue = '';
  initialDateValue = '';

  private fb = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private errorHandlerService = inject(ErrorHandlerService);
  private cancellation = new Subject<void>();

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  get isSubmitDisabled(): boolean {
    return this.isLoading || this.dateValue === this.initialDateValue || this.rescheduleForm.invalid;
  }

  get dateFormat(): string {
    return this.enableTime ? 'Y-m-d H:i' : 'Y-m-d';
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  onCancel(): void {
    this.closeModal();
  }

  onDateChange(value: string | undefined): void {
    this.dateValue = value ?? '';
    this.rescheduleForm.patchValue({ date: this.dateValue });
    this.rescheduleForm.get('date')?.markAsTouched();
  }

  async onSubmit(): Promise<void> {
    if (this.rescheduleForm.invalid || this.isSubmitDisabled || !this.submitHandler) {
      this.rescheduleForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    try {
      await this.submitHandler({
        dueDate: this.dateValue,
        notifyPersonResponsible: this.rescheduleForm.value.notifyPersonResponsible
      }, this.cancellation);
      this.toastr.success(this.successMessage);
      this.activeModal.close(true);
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private initializeForm(): void {
    const currentDate = this.initialDate ? new Date(this.initialDate) : new Date();

    this.dateValue = this.formatDate(currentDate);
    this.initialDateValue = this.dateValue;

    this.rescheduleForm = this.fb.group({
      date: [this.dateValue, Validators.required],
      notifyPersonResponsible: [false]
    });
  }

  private formatDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      date = new Date();
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (!this.enableTime) {
      return `${year}-${month}-${day}`;
    }

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
}